import { memo, useCallback, useEffect } from 'react';

import { useTrackAgentChatRunSettled, useTrackAgentChatRunStalled, useTrackAgentChatRunStarted } from '@air/analytics';

import { useRunLifecycleSubscription } from '../../hooks/useEventSubscription';
import { useAgentManager } from '../../providers/AgentProvider';
import { type RunLifecyclePayload } from '../../types/subscriptions';

// Guarded for SSR; a hidden tab at emit time points at laptop-sleep/backgrounding.
const isTabHidden = () => typeof document !== 'undefined' && document.hidden;

/**
 * Renderless bridge contributing only what the manager's run-health machinery can't
 * reach itself: forwards run-lifecycle events to the analytics hooks (which need React
 * context), and re-runs the idle check on tab-return/reconnect (in-page timers under-fire
 * in hidden tabs).
 */
export const AgentRunLifecycleBridge = memo(() => {
  const { manager, resourceId } = useAgentManager();
  const { trackAgentChatRunStarted } = useTrackAgentChatRunStarted();
  const { trackAgentChatRunSettled } = useTrackAgentChatRunSettled();
  const { trackAgentChatRunStalled } = useTrackAgentChatRunStalled();

  const handleRunLifecycle = useCallback(
    (payload: RunLifecyclePayload) => {
      switch (payload.phase) {
        case 'started': {
          trackAgentChatRunStarted({
            action: payload.actionName,
            canvas_id: resourceId,
            run_id: payload.runId,
            thread_id: payload.threadId,
          });

          return;
        }

        case 'stalled': {
          trackAgentChatRunStalled({
            action: payload.actionName,
            canvas_id: resourceId,
            elapsed_ms: payload.elapsedMs,
            result_arrived: payload.resultArrived,
            run_finished_received: payload.runFinishedReceived,
            run_id: payload.runId,
            tab_hidden: isTabHidden(),
            thread_id: payload.threadId,
            trigger: payload.trigger,
          });

          return;
        }

        case 'settled': {
          trackAgentChatRunSettled({
            action: payload.actionName,
            canvas_id: resourceId,
            duration_ms: payload.durationMs,
            outcome: payload.outcome,
            result_arrived: payload.resultArrived,
            run_finished_received: payload.runFinishedReceived,
            run_id: payload.runId,
            tab_hidden: isTabHidden(),
            thread_id: payload.threadId,
            ...(payload.requestedVariantCount !== undefined && {
              variant_count_delivered: payload.deliveredVariantCount,
              variant_count_requested: payload.requestedVariantCount,
              variant_count_source: payload.variantCountSource,
            }),
          });

          return;
        }
      }
    },
    [resourceId, trackAgentChatRunSettled, trackAgentChatRunStalled, trackAgentChatRunStarted],
  );

  useRunLifecycleSubscription(handleRunLifecycle);

  useEffect(() => {
    if (!manager) {
      return;
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        manager.sweepStalledRuns('tab_return');
      }
    };

    const handleOnline = () => {
      manager.sweepStalledRuns('reconnect');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [manager]);

  return null;
});

AgentRunLifecycleBridge.displayName = 'AgentRunLifecycleBridge';
